import { useState } from 'react';
import { aboutInfo } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u4ogd3pcjqs2fcsm6g';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl) {
      const honeypotValue = honeypotEl.value.trim();
      if (honeypotValue) {
        setFormStatus('success');
        form.reset();
        return;
      }
      formData.delete('phone_alt');
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const organizationSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'Organization',
    name: '潘金莲文化研究中心',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: aboutInfo.slogan,
    email: aboutInfo.contactEmail,
    telephone: aboutInfo.contactPhone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '中关村南大街5号 文华大厦B座12层',
      addressLocality: '北京',
      addressRegion: '海淀区',
      addressCountry: 'CN',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <section className="pt-24 pb-8 md:pb-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 text-lg">{aboutInfo.slogan}</p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  品牌介绍
                </h2>
                {aboutInfo.introduction.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="lg:w-80 flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20traditional%20Chinese%20scholar%20study%20room%20with%20ancient%20manuscripts%20and%20calligraphy%20scrolls%2C%20warm%20natural%20light%20through%20paper%20windows%2C%20cultural%20research%20atmosphere%2C%20refined%20academic%20setting%2C%20classical%20Chinese%20aesthetic&width=800&height=600&seq=about-brand&orientation=portrait"
                  alt="潘金莲文化研究中心"
                  className="w-full rounded-xl object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation & Stats */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3 text-center">
              学术信誉
            </h2>
            <p className="text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              以严谨的学术态度和开放的文化视野，赢得学界与公众的信赖
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.reputation.map((item) => (
                <div
                  key={item.label}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading mb-2">
                    {item.value}
                    <span className="text-lg font-normal text-foreground-500 ml-1">{item.suffix}</span>
                  </div>
                  <div className="text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-bold font-heading text-foreground-950 mb-4">研究团队</h3>
              <p className="text-foreground-700 leading-relaxed">
                {aboutInfo.teamIntro}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              如有任何学术合作、研究咨询或媒体采访需求，欢迎与我们取得联系
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contactEmail}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">联系电话</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contactPhone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contactAddress}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all text-foreground-900 placeholder:text-foreground-400"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all text-foreground-900 placeholder:text-foreground-400"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all text-foreground-900 placeholder:text-foreground-400"
                    placeholder="请输入邮件主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all text-foreground-900 placeholder:text-foreground-400 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                {formStatus === 'error' && formError && (
                  <div className="text-sm text-accent-600 bg-accent-50 px-4 py-2.5 rounded-lg">
                    {formError}
                  </div>
                )}
                {formStatus === 'success' && (
                  <div className="text-sm text-green-700 bg-green-50 px-4 py-2.5 rounded-lg">
                    感谢您的留言，我们会尽快与您联系！
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
                >
                  {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                </button>
              </form>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}